import { useToast } from '@cad-arc';
import { useMutation } from '@tanstack/react-query';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { ApiResult, BoloResource } from '@mark43/cad-api';
import { useExportsApi } from '../../../exports/use-exports-api';
import { errorOccurredWhile } from '../../../common/helpers/error-occurred-while';
import { useCallback } from 'react';
import { AxiosError } from 'axios-cad-nx';

export function useGenerateBoloPdf() {
    const client = useApiClient();
    const toast = useToast();
    const { refetchExports } = useExportsApi();

    const generatePdfMutation = useMutation(
        async ({
            boloId,
            includeAttachments,
        }: {
            boloId: number;
            includeAttachments: boolean;
        }) => {
            const generateExport = (withAttachments: boolean = false) => {
                return client.request<BoloResource.ExportPdf>({
                    method: 'POST',
                    url: `bolo/export`,
                    data: {
                        boloId,
                        includeAttachments: withAttachments,
                    },
                });
            };

            try {
                await generateExport(includeAttachments);
                refetchExports();
            } catch (error) {
                const handleError = createApiErrorHandler(() =>
                    toast({
                        status: 'error',
                        description: errorOccurredWhile('exporting BOLO'),
                    })
                );
                handleError(error as AxiosError<ApiResult, unknown>);
            }
        }
    );

    const generatePDFWithoutAttachments = useCallback(
        function generatePDFWithoutAttachments(boloId: number): void {
            generatePdfMutation.mutate({ boloId, includeAttachments: false });
        },
        [generatePdfMutation]
    );

    const generatePDFWithAttachments = useCallback(
        function generatePDFWithAttachments(boloId: number): void {
            generatePdfMutation.mutate({ boloId, includeAttachments: true });
        },
        [generatePdfMutation]
    );

    return {
        generatePDFWithoutAttachments,
        generatePDFWithAttachments,
        isLoading: generatePdfMutation.isLoading,
    };
}
